import { useCallback, useEffect, useRef, useState } from "react";

import type { ScaffoldEvent } from "@/lib/local-types";
import { logger } from "@/lib/logger";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;
const POLL_INTERVAL_MS = 5000;

interface UseScaffoldEventsResult {
  events: ScaffoldEvent[];
  /** Log lines grouped by step key (e.g. "scaffolding", "pushing"). */
  logsByStep: Record<string, string[]>;
  connected: boolean;
  done: boolean;
  error: string | null;
}

export function useScaffoldEvents(projectId: number | null): UseScaffoldEventsResult {
  const [events, setEvents] = useState<ScaffoldEvent[]>([]);
  const [logsByStep, setLogsByStep] = useState<Record<string, string[]>>({});
  const [connected, setConnected] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const retriesRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const doneRef = useRef(false);
  // Latest connectSSE — used by the SSE retry timer to avoid referencing the
  // useCallback before its declaration (circular self-reference would trip
  // react-hooks/immutability).
  const connectSSERef = useRef<(pid: number) => void>(() => {});

  // Keep doneRef in sync so callbacks can check terminal state
  useEffect(() => {
    doneRef.current = done;
  }, [done]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    stopPolling();
  }, [stopPolling]);

  /** Fallback: poll GET /api/projects/{id} to detect terminal status. */
  const startPolling = useCallback(
    (pid: number) => {
      if (pollTimerRef.current) {
        return;
      } // already polling

      logger.warn("[sse] falling back to polling for project", pid);
      pollTimerRef.current = setInterval(async () => {
        if (doneRef.current) {
          stopPolling();
          return;
        }
        try {
          const resp = await fetch(`/api/projects/${pid}`);
          if (!resp.ok) {
            return;
          }

          const project = await resp.json();
          if (project.status === "completed") {
            const railwayFailed = project.include_railway && !!project.railway_error_message;
            setEvents((prev) => [
              ...prev,
              {
                type: "completed",
                github_url: project.github_url,
                supabase_url: project.supabase_dashboard_url ?? undefined,
                railway_url: project.railway_dashboard_url ?? undefined,
                app_url: project.app_url ?? undefined,
                message: railwayFailed
                  ? `Project created, but Railway setup failed: ${project.railway_error_message}`
                  : "Project created successfully!",
              },
            ]);
            setDone(true);
            stopPolling();
          } else if (project.status === "failed") {
            setEvents((prev) => [
              ...prev,
              { type: "failed", message: project.error_message ?? "Unknown error" },
            ]);
            setError(project.error_message ?? "Unknown error");
            setDone(true);
            stopPolling();
          } else if (project.status === "cancelled") {
            const message = project.error_message ?? "Cancelled by user";
            setEvents((prev) => [...prev, { type: "cancelled", message }]);
            setError(message);
            setDone(true);
            stopPolling();
          }
        } catch {
          logger.error("[poll] failed to fetch project status for", pid);
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling],
  );

  const connectSSE = useCallback(
    (pid: number) => {
      if (doneRef.current) {
        return;
      }

      const es = new EventSource(`/api/projects/${pid}/events`);
      eventSourceRef.current = es;

      es.onopen = () => {
        setConnected(true);
        retriesRef.current = 0; // Reset retries on successful connection
        stopPolling(); // Stop polling if it was running

        // Guard against late subscription: if the project already finished
        // before we subscribed, the SSE stream will stay open but silent.
        // Do a one-shot REST check to detect terminal status.
        fetch(`/api/projects/${pid}`)
          .then((resp) => (resp.ok ? resp.json() : null))
          .then((project) => {
            if (!project || doneRef.current) {
              return;
            }
            if (project.status === "completed") {
              const railwayFailed = project.include_railway && !!project.railway_error_message;
              setEvents((prev) => [
                ...prev,
                {
                  type: "completed",
                  github_url: project.github_url,
                  supabase_url: project.supabase_dashboard_url ?? undefined,
                  railway_url: project.railway_dashboard_url ?? undefined,
                  app_url: project.app_url ?? undefined,
                  message: railwayFailed
                    ? `Project created, but Railway setup failed: ${project.railway_error_message}`
                    : "Project created successfully!",
                },
              ]);
              setDone(true);
              cleanup();
            } else if (project.status === "failed") {
              setEvents((prev) => [
                ...prev,
                { type: "failed", message: project.error_message ?? "Unknown error" },
              ]);
              setError(project.error_message ?? "Unknown error");
              setDone(true);
              cleanup();
            } else if (project.status === "cancelled") {
              const message = project.error_message ?? "Cancelled by user";
              setEvents((prev) => [...prev, { type: "cancelled", message }]);
              setError(message);
              setDone(true);
              cleanup();
            }
          })
          .catch(() => {
            logger.warn("[sse] onopen status check failed for project", pid);
          });
      };

      es.addEventListener("step", (e) => {
        try {
          const data = JSON.parse(e.data) as ScaffoldEvent;
          setEvents((prev) => [...prev, data]);
        } catch {
          logger.error("[sse] failed to parse step event:", e.data);
        }
      });

      es.addEventListener("log", (e) => {
        try {
          const data = JSON.parse(e.data) as ScaffoldEvent;
          if (data.line && data.step) {
            setLogsByStep((prev) => ({
              ...prev,
              [data.step!]: [...(prev[data.step!] ?? []), data.line!],
            }));
          }
        } catch {
          logger.error("[sse] failed to parse log event:", e.data);
        }
      });

      es.addEventListener("completed", (e) => {
        try {
          const data = JSON.parse(e.data) as ScaffoldEvent;
          setEvents((prev) => [...prev, data]);
          setDone(true);
          cleanup();
        } catch {
          logger.error("[sse] failed to parse completed event:", e.data);
        }
      });

      es.addEventListener("failed", (e) => {
        try {
          const data = JSON.parse(e.data) as ScaffoldEvent;
          setEvents((prev) => [...prev, data]);
          setError(data.message ?? "Unknown error");
          setDone(true);
          cleanup();
        } catch {
          logger.error("[sse] failed to parse failed event:", e.data);
        }
      });

      es.addEventListener("cancelled", (e) => {
        try {
          const data = JSON.parse(e.data) as ScaffoldEvent;
          const message = data.message ?? "Cancelled by user";
          setEvents((prev) => [...prev, { ...data, type: "cancelled", message }]);
          setError(message);
          setDone(true);
          cleanup();
        } catch {
          logger.error("[sse] failed to parse cancelled event:", e.data);
        }
      });

      es.onerror = () => {
        logger.error("[sse] connection error for project", pid);
        setConnected(false);

        // Close the broken connection
        es.close();
        eventSourceRef.current = null;

        if (doneRef.current) {
          return;
        }

        retriesRef.current += 1;
        if (retriesRef.current <= MAX_RETRIES) {
          logger.warn(
            `[sse] retrying (${retriesRef.current}/${MAX_RETRIES}) in ${RETRY_DELAY_MS}ms`,
          );
          if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
          }
          retryTimerRef.current = setTimeout(() => connectSSERef.current(pid), RETRY_DELAY_MS);
        } else {
          // Exhausted retries — fall back to polling
          startPolling(pid);
        }
      };
    },
    [cleanup, stopPolling, startPolling],
  );

  useEffect(() => {
    connectSSERef.current = connectSSE;
  });

  useEffect(() => {
    if (projectId === null) {
      return;
    }

    cleanup();
    retriesRef.current = 0;
    connectSSE(projectId);

    return cleanup;
  }, [projectId, cleanup, connectSSE]);

  return { events, logsByStep, connected, done, error };
}
